import { OnboardingState } from '@developerz/domain';
import { useSearchParams } from '@solidjs/router';
import { createMemo, type JSX, Match, Show, Switch } from 'solid-js';
import { DeniedState } from '../components/DeniedState';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { OnboardingBrand } from '../components/OnboardingBrand';
import { OnboardingReturnNotice } from '../components/OnboardingReturnNotice';
import { OnboardingStepCard } from '../components/OnboardingStepCard';
import { OnboardingSteps } from '../components/OnboardingSteps';
import { OnboardingTerminal } from '../components/OnboardingTerminal';
import { PageHeader } from '../components/PageHeader';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { t } from '../i18n';
import { isForbidden } from '../lib/api';
import { useSeatOffer, useSeatOfferSettled } from '../lib/billing';
import {
  displayedOnboardingState,
  onboardingStepLabels,
  STATE_STEP,
  useOnboardingState,
} from '../lib/onboarding';
import { createOnboardingActions } from '../lib/onboardingActions';
import { SESSION_TOKEN } from '../lib/session';
import { createToast } from '../lib/toast';

// Onboarding wizard — state-driven. Fetches `/v1/onboarding/state`, renders the
// active step's panel, and drives `/v1/onboarding/advance`. The transition table
// itself lives in `@developerz/domain`; this just reflects the server's state.
// Layout = the canvas ONBOARDING artboard: brand, header, then the step rail
// beside the active step's panel (the terminal screen at `active`).
//
// NOT `<QueryBoundary/>`, and the `<Switch>` below says why: this screen has a
// signed-out arm the boundary has no slot for, and it deliberately orders DATA
// ABOVE ERROR (the funnel polls every 5s, so a boundary's error-first ordering
// replaces a populated wizard with an alert on one 502). What it DOES take from
// the boundary is the 403 rule — `<DeniedState/>` selected from the error,
// above the retryable arm.

/** `token` is injectable for tests; the router mounts this with no props → dev session. */
export function Onboarding(props: { token?: string | null }): JSX.Element {
  // `=== undefined`, never `??`: an explicit `null` MEANS signed out and must
  // survive. A prod build's `SESSION_TOKEN` is the non-null `__cookie_session__`
  // marker, so `??` would swallow that null and leave the no-session branch
  // below unreachable anywhere but a test (where the marker happens to be null).
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const hasSession = token !== null;
  const query = useOnboardingState(token);
  const { toast, notify, dismiss } = createToast();
  const actions = createOnboardingActions({
    token,
    state: () => query.data?.state,
    refetch: () => query.refetch(),
    notify,
  });
  const [params] = useSearchParams();
  // The funnel's one conditional step: paid activation, shown while the account
  // still has no seat plan and this caller may buy one. Same gate as the card
  // that fills it (`OnboardingBuySeats`) — memoised so the rail keeps one label
  // list per (offer) state instead of a fresh array per read.
  const seatsPending = useSeatOffer(token);
  const seatsSettled = useSeatOfferSettled(token);
  const stepLabels = createMemo(() => onboardingStepLabels(seatsPending()));

  return (
    <section class="onboarding">
      <div class="onboarding__col">
        <OnboardingBrand token={token} />

        <PageHeader
          title={t('onboarding.title')}
          desc={t('onboarding.subtitle', { count: stepLabels().length })}
        />

        <OnboardingReturnNotice ghSetup={params['gh_setup']} />

        <Switch
          fallback={
            /* Real sr-only TEXT, not `aria-label` — the rule `<QueryBoundary/>`
               already states: a `role="status"` announcement is driven by
               text-content changes, so a label on an otherwise text-free box of
               skeletons announced nothing while the funnel resolved. Same key,
               both locales; no new string. */
            <div class="onboarding__loading" role="status">
              <span class="sr-only">{t('onboarding.loading')}</span>
              <Skeleton height="1.85rem" />
              <Skeleton class="skeleton--card" />
            </div>
          }
        >
          <Match when={!hasSession}>
            <EmptyState message={t('onboarding.no_session')} />
          </Match>
          {/* DATA FIRST. TanStack keeps the last good `data` across a failed
              refetch, and this wizard polls every 5s on three of its steps — so
              ordering the error branch first replaced a fully-populated funnel
              with a destructive alert on one 502. Error only paints while
              nothing is on screen; with data it is a banner above the step. */}
          <Match when={query.data}>
            {(status) => (
              <>
                <Show when={query.isError}>
                  <div class="alert alert--icon" role="status">
                    <p class="alert__title">{t('onboarding.stale')}</p>
                  </div>
                </Show>
                <div class="onboarding__layout">
                  <Show when={seatsSettled()} fallback={<Skeleton class="skeleton--card" />}>
                    <OnboardingSteps
                      labels={stepLabels()}
                      current={STATE_STEP[displayedOnboardingState(status())]}
                    />
                  </Show>
                  <div class="onboarding__main">
                    <Show
                      when={status().state !== OnboardingState.Active}
                      fallback={<OnboardingTerminal token={token} />}
                    >
                      <OnboardingStepCard
                        token={token}
                        state={displayedOnboardingState(status())}
                        displayOnly={displayedOnboardingState(status()) !== status().state}
                        step={STATE_STEP[displayedOnboardingState(status())] + 1}
                        stepCount={stepLabels().length}
                        busy={actions.busy()}
                        onAdvance={actions.advance}
                        onCopy={actions.copy}
                        onRecheck={() => void query.refetch()}
                      />
                    </Show>
                  </div>
                </div>
              </>
            )}
          </Match>
          {/* ORDER IS LOAD-BEARING, and for the reason `<QueryBoundary/>` states:
              `<Switch>` takes the FIRST match, so the 403 arm must sit above the
              general error arm — below it, every denial falls through to
              `<ErrorState/>` and gets the Retry that is the whole point of
              splitting them. A denial is an ANSWER: the second call carries the
              same scopes and the same `X-Dz-Account` claim and is refused
              identically, so a Try again here could only ever 403 again. */}
          <Match when={query.isError && isForbidden(query.error)}>
            <DeniedState error={query.error} />
          </Match>
          <Match when={query.isError}>
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </Match>
        </Switch>
      </div>

      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'error'}
        onDismiss={dismiss}
      />
    </section>
  );
}
